import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Truck, Plus, FileText, Loader2, Receipt, X, ArrowUp, ArrowDown, CheckCircle2, Send, ClipboardCheck, Printer, ChevronDown, Activity, AlertCircle, Clock, PackagePlus, Search, ArrowRightLeft, RotateCcw, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

const STATUS_META: Record<string, { label: string; cor: string }> = {
  aberta:    { label: 'Aberta',    cor: 'bg-amber-100 text-amber-800' },
  faturada:  { label: 'Faturada',  cor: 'bg-cyan-100 text-cyan-800' },
  em_rota:   { label: 'Em rota',   cor: 'bg-blue-100 text-blue-800' },
  em_acerto: { label: 'Em acerto', cor: 'bg-violet-100 text-violet-800' },
  fechada:   { label: 'Fechada',   cor: 'bg-emerald-100 text-emerald-800' },
  cancelada: { label: 'Cancelada', cor: 'bg-ink-100 text-ink-600' },
}

// Anel de progresso do acerto (% já conferido/salvo) — pra listagem de cargas em aberto
function AcertoRing({ pct }: { pct: number }) {
  const SZ = 38, r = 15, circ = 2 * Math.PI * r
  const off = circ * (1 - Math.max(0, Math.min(100, pct)) / 100)
  const cor = pct >= 100 ? '#059669' : pct >= 50 ? '#7c3aed' : '#f59e0b'
  return (
    <span className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: SZ, height: SZ }} title={`Acerto ${pct}% conferido (rascunho salvo)`}>
      <svg width={SZ} height={SZ} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={SZ / 2} cy={SZ / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth="3.5" />
        <circle cx={SZ / 2} cy={SZ / 2} r={r} fill="none" stroke={cor} strokeWidth="3.5"
          strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <span className="absolute text-[9px] font-bold text-ink-700">{pct}%</span>
    </span>
  )
}

export default function CargasDistribuicaoPage() {
  const qc = useQueryClient()
  const [novaAberta, setNovaAberta] = useState(false)
  const [acertarSel, setAcertarSel] = useState<any | null>(null)
  const [filtroStatus, setFiltroStatus] = useState<'aberto' | 'fechada' | 'todas'>('aberto')

  const sairRota = useMutation({
    mutationFn: async (id: number) => (await api.post(`/vendas/cargas/${id}/sair-rota`)).data,
    onSuccess: () => { toast.success('Carga em rota'); qc.invalidateQueries({ queryKey: ['cargas-dist'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const cancelarCarga = useMutation({
    mutationFn: async (id: number) => (await api.post(`/vendas/cargas/${id}/cancelar`)).data,
    onSuccess: (d: any) => {
      const n = (d?.pedidos_revertidos || []).length + (d?.pedidos_soltos || []).length
      toast.success(`Carga cancelada — ${n} pedido(s) liberado(s) pra remontar`)
      qc.invalidateQueries({ queryKey: ['cargas-dist'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar carga'),
  })
  const estornarAcerto = useMutation({
    mutationFn: async (id: number) => (await api.post(`/vendas/cargas/${id}/estornar-acerto`)).data,
    onSuccess: (d: any) => {
      toast.success(`Acerto cancelado — ${d?.pedidos_reabertos || 0} entrega(s) reaberta(s)${d?.devolucoes_mantidas ? `, ${d.devolucoes_mantidas} devolução(ões) mantida(s)` : ''}. Refaça o acerto.`, { duration: 7000 })
      qc.invalidateQueries({ queryKey: ['cargas-dist'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar acerto', { duration: 7000 }),
  })
  const baixarResumo = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/resumo-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error(`Erro no resumo da carga #${numero}`) }
  }

  const { data: cargas = [], isLoading } = useQuery<any[]>({
    queryKey: ['cargas-dist'],
    queryFn: async () => (await api.get('/vendas/cargas/', { params: { tipo: 'distribuicao' } })).data,
  })

  const faturar = useMutation({
    mutationFn: async ({ id, sem_nota_ids, motorista_id, veiculo_id, conta_bancaria_id }: { id: number; sem_nota_ids: number[]; motorista_id: number | null; veiculo_id: number | null; conta_bancaria_id?: number | null }) =>
      (await api.post(`/vendas/cargas/${id}/faturar`, { sem_nota_ids, motorista_id, veiculo_id, conta_bancaria_id }, { timeout: 60000 })).data,
    onSuccess: (d: any, vars: any) => {
      toast.success(`Faturamento iniciado — acompanhe o progresso (${d.total} pedidos)`, { duration: 4000 })
      setFaturarCarga(null)
      setMonitorCargaId(vars.id)  // abre o monitor pedido-a-pedido
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao faturar carga'),
  })
  const [faturarCarga, setFaturarCarga] = useState<any>(null)
  const [monitorCargaId, setMonitorCargaId] = useState<number | null>(null)
  const [incluirCarga, setIncluirCarga] = useState<any>(null)

  const editarNomeCarga = async (c: any) => {
    const novo = window.prompt(`Nome da carga #${c.numero} (rota/identificação):`, c.nome || '')
    if (novo === null) return
    try {
      await api.patch(`/vendas/cargas/${c.id}`, { nome: novo.trim() })
      toast.success('Nome atualizado')
      qc.invalidateQueries({ queryKey: ['cargas-dist'] })
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao salvar') }
  }

  const baixarRomaneio = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/romaneio-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error(`Erro ao gerar romaneio da carga #${numero}`) }
  }

  // PDF único com DANFEs + pedidos simples + romaneio + boletos, ordenado por cidade
  const imprimirTudo = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/imprimir-tudo-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) {
      toast.error(e?.response?.status === 404 ? `Carga #${numero}: nada pra imprimir` : `Erro ao imprimir tudo da carga #${numero}`)
    }
  }

  // Acerto da carga (estilo WinThor rotina 410) — pra fechar com motorista
  const imprimirAcerto = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/imprimir-acerto-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error(`Erro ao gerar acerto da carga #${numero}`) }
  }

  // Mapa de separação (picking list) — mesmo PDF do "Reimprimir" das Ondas.
  const baixarSeparacao = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/mapa-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error(`Erro ao gerar separação da carga #${numero}`) }
  }

  // Pedidos da carga (PDF único, um pedido por página) — pra emitir junto com as notas.
  const baixarPedidos = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/pedidos-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { toast.error(`Erro ao gerar pedidos da carga #${numero}`) }
  }

  // Pré-visualização das notas (ver impostos antes de faturar) — não consome número/chave.
  const previewNotas = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/preview-notas-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) {
      toast.error(e?.response?.status === 404 ? `Carga #${numero}: não deu pra gerar o preview` : `Erro no preview da carga #${numero}`)
    }
  }

  // Pré-visualização dos boletos da carga (SIMULAÇÃO) — valor já com ST, sem consumir sequência.
  const previewBoletos = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/boletos-preview-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) {
      toast.error(e?.response?.status === 404 ? `Carga #${numero} sem pedido com boleto` : `Erro nos boletos da carga #${numero}`)
    }
  }

  // Notas (DANFEs) da carga em lote — junta as NF-e autorizadas num PDF.
  const baixarNotas = async (id: number, numero: number) => {
    try {
      const r = await api.get(`/vendas/cargas/${id}/notas-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) {
      toast.error(e?.response?.status === 404 ? `Carga #${numero} sem NF-e autorizada` : `Erro ao gerar notas da carga #${numero}`)
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2"><Truck size={22} /> Cargas de Distribuição</h1>
        <button onClick={() => setNovaAberta(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium">
          <Plus size={16} /> Nova carga
        </button>
      </div>

      {(() => {
        const EM_ABERTO = ['aberta', 'faturada', 'em_rota', 'em_acerto']
        const naRua = cargas.filter((c) => ['em_rota', 'em_acerto'].includes(c.status)).length
        const emAberto = cargas.filter((c) => EM_ABERTO.includes(c.status)).length
        return (
          <div className="flex flex-wrap items-center gap-2">
            {([['aberto', `Em aberto (${emAberto})`], ['fechada', 'Fechadas'], ['todas', 'Todas']] as const).map(([v, label]) => (
              <button key={v} onClick={() => setFiltroStatus(v as any)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filtroStatus === v ? 'bg-brand-600 text-white' : 'border border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                {label}
              </button>
            ))}
            {naRua > 0 && <span className="ml-2 text-sm text-blue-700">🚚 {naRua} na rua</span>}
          </div>
        )
      })()}

      {(() => {
        const EM_ABERTO = ['aberta', 'faturada', 'em_rota', 'em_acerto']
        const filtradas = cargas.filter((c) =>
          filtroStatus === 'todas' ? true
            : filtroStatus === 'fechada' ? ['fechada', 'cancelada'].includes(c.status)
              : EM_ABERTO.includes(c.status))
        if (isLoading) return <div className="p-8 text-center text-ink-500">Carregando...</div>
        if (filtradas.length === 0) return <div className="card card-body text-center text-ink-500">Nenhuma carga nesse filtro.</div>
        return (
        <div className="table-wrap">
          <table className="table text-sm">
            <thead>
              <tr><th>#</th><th>Status</th><th>Paradas</th><th>Saída</th><th className="text-right">Ações</th></tr>
            </thead>
            <tbody>
              {filtradas.map((c) => {
                const meta = STATUS_META[c.status] || { label: c.status, cor: 'bg-ink-100 text-ink-600' }
                return (
                  <tr key={c.id}>
                    <td className="font-medium">
                      <span>#{c.numero}</span>
                      <span className="text-ink-400 mx-1">·</span>
                      <button onClick={() => editarNomeCarga(c)}
                        className="hover:text-brand-700 hover:underline text-left"
                        title="Clique pra editar o nome">
                        {c.nome || <span className="text-ink-400 italic">— editar —</span>}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.cor}`}>{meta.label}</span>
                        {c.status !== 'fechada' && c.acerto_pct != null && <AcertoRing pct={c.acerto_pct} />}
                      </div>
                    </td>
                    <td>{c.total_pedidos}</td>
                    <td>{c.data_saida ? new Date(c.data_saida).toLocaleString('pt-BR') : '—'}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {(() => {
                          const ab = c.status === 'aberta'
                          const fat = ['faturada', 'em_rota', 'em_acerto'].includes(c.status)
                          const items: { label: string; onClick: () => void }[] = []
                          items.push({ label: 'Imprimir tudo', onClick: () => imprimirTudo(c.id, c.numero) })
                          if (['faturada', 'em_rota', 'em_acerto', 'fechada'].includes(c.status))
                            items.push({ label: 'Imprimir Acerto', onClick: () => imprimirAcerto(c.id, c.numero) })
                          if (ab || fat) {
                            items.push({ label: 'Imprimir separação', onClick: () => baixarSeparacao(c.id, c.numero) })
                            items.push({ label: 'Imprimir pedidos', onClick: () => baixarPedidos(c.id, c.numero) })
                          }
                          if (ab) {
                            items.push({ label: 'Pré-visualizar notas (impostos)', onClick: () => previewNotas(c.id, c.numero) })
                            items.push({ label: 'Pré-visualizar boletos', onClick: () => previewBoletos(c.id, c.numero) })
                          }
                          if (fat) items.push({ label: 'Imprimir notas (DANFE)', onClick: () => baixarNotas(c.id, c.numero) })
                          if (['faturada', 'em_rota', 'em_acerto', 'fechada'].includes(c.status))
                            items.push({ label: 'Romaneio', onClick: () => baixarRomaneio(c.id, c.numero) })
                          if (c.status === 'fechada') items.push({ label: 'Resumo da carga', onClick: () => baixarResumo(c.id, c.numero) })
                          return <ImpressoesMenuCarga items={items} />
                        })()}
                        {c.status === 'aberta' && (
                          <button onClick={() => setFaturarCarga(c)} disabled={faturar.isPending}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">
                            <Receipt size={14} /> Faturar em lote
                          </button>
                        )}
                        {['aberta', 'faturada'].includes(c.status) && (
                          <button onClick={() => setMonitorCargaId(c.id)} title="Acompanhar faturamento pedido-a-pedido"
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium">
                            <Activity size={14} /> Monitor
                          </button>
                        )}
                        {['faturada', 'em_rota'].includes(c.status) && (
                          <button onClick={() => setIncluirCarga(c)} title="Incluir uma nota já faturada nesta carga (inclusão/transferência)"
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium">
                            <PackagePlus size={14} /> Incluir nota
                          </button>
                        )}
                        {c.status === 'aberta' && (
                          <button onClick={() => { if (confirm(`Cancelar a carga #${c.numero}? Os pedidos voltam para a fila de montagem.`)) cancelarCarga.mutate(c.id) }}
                            disabled={cancelarCarga.isPending}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-medium disabled:opacity-50">
                            <X size={14} /> Cancelar
                          </button>
                        )}
                        {c.status === 'faturada' && (
                          <button onClick={() => sairRota.mutate(c.id)} disabled={sairRota.isPending}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium disabled:opacity-50">
                            <Send size={14} /> Sair p/ rota
                          </button>
                        )}
                        {['em_rota', 'em_acerto'].includes(c.status) && (
                          <button onClick={() => setAcertarSel(c)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium">
                            <ClipboardCheck size={14} /> Acertar
                          </button>
                        )}
                        {c.status === 'fechada' && (
                          <button onClick={() => { if (confirm(`Cancelar o acerto da carga #${c.numero}?\n\nIsso desfaz só o FINANCEIRO (formas de pagamento, caixa, contas) pra você refazer o acerto. Devoluções/notas NÃO são tocadas.`)) estornarAcerto.mutate(c.id) }}
                            disabled={estornarAcerto.isPending}
                            title="Desfaz o acerto (financeiro) pra refazer as formas de pagamento. Não mexe em devolução/NF."
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-medium disabled:opacity-50">
                            <RotateCcw size={14} /> Cancelar acerto
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        )
      })()}

      {novaAberta && <ModalNovaCarga onClose={() => setNovaAberta(false)} onCriada={() => { setNovaAberta(false); qc.invalidateQueries({ queryKey: ['cargas-dist'] }) }} />}
      {acertarSel && <ModalAcerto carga={acertarSel} onClose={() => setAcertarSel(null)} onDone={() => { setAcertarSel(null); qc.invalidateQueries({ queryKey: ['cargas-dist'] }) }} />}
      {faturarCarga && <ModalFaturar carga={faturarCarga} faturando={faturar.isPending}
        onClose={() => setFaturarCarga(null)}
        onFaturar={(semNotaIds: number[], motoristaId: number | null, veiculoId: number | null, bancoBoletoId: number | null) =>
          faturar.mutate({ id: faturarCarga.id, sem_nota_ids: semNotaIds, motorista_id: motoristaId, veiculo_id: veiculoId, conta_bancaria_id: bancoBoletoId })} />}
      {monitorCargaId != null && <ModalProgressoFaturamento cargaId={monitorCargaId}
        onClose={() => { setMonitorCargaId(null); qc.invalidateQueries({ queryKey: ['cargas-dist'] }) }} />}
      {incluirCarga && <ModalIncluirNota carga={incluirCarga}
        onClose={() => setIncluirCarga(null)}
        onDone={() => { setIncluirCarga(null); qc.invalidateQueries({ queryKey: ['cargas-dist'] }) }} />}
    </div>
  )
}

// Inclui/transfere uma nota JÁ FATURADA numa carga já faturada (entra no romaneio/MDF-e/acerto).
// Busca pedidos faturados soltos ou de outra carga; marcar e incluir.
function ModalIncluirNota({ carga, onClose, onDone }: { carga: any; onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<Set<number>>(new Set())
  const { data, isFetching } = useQuery<any>({
    queryKey: ['incluiveis', carga.id, q],
    queryFn: async () => (await api.get(`/vendas/cargas/${carga.id}/pedidos-incluiveis`, { params: { q } })).data,
  })
  const lista: any[] = data?.pedidos || []
  const toggle = (id: number) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const incluir = useMutation({
    mutationFn: async () => (await api.post(`/vendas/cargas/${carga.id}/incluir-pedidos`, { pedido_ids: Array.from(sel) })).data,
    onSuccess: (r: any) => {
      toast.success(`${r.total} nota(s) incluída(s)${r.transferidos?.length ? ` · ${r.transferidos.length} transferida(s)` : ''}`, { duration: 5000 })
      if (r.ignorados?.length) toast.error(`${r.ignorados.length} ignorada(s): ${r.ignorados.map((i: any) => i.motivo).slice(0, 2).join('; ')}`, { duration: 8000 })
      onDone()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao incluir'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2 font-semibold text-ink-800">
            <PackagePlus size={18} className="text-amber-600" /> Incluir nota na Carga #{carga.numero ?? carga.id}
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={20} /></button>
        </div>
        <div className="px-5 pt-4">
          <p className="text-xs text-ink-500 mb-2">Notas <strong>já faturadas</strong> (com NF ou simples) que estão soltas ou em outra carga. Marque pra incluir — entram no romaneio, MDF-e e acerto desta carga. Não refatura nada.</p>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={enterAbrePrimeiro(lista, (p: any) => toggle(p.pedido_id))}
              placeholder="Buscar por nº do pedido, nº da NF ou cliente…"
              className="input w-full pl-9" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3 mt-1">
          {isFetching && <div className="p-4 text-center text-ink-400"><Loader2 className="animate-spin inline" size={16} /></div>}
          {!isFetching && lista.length === 0 && <div className="p-6 text-center text-ink-400 text-sm">Nenhuma nota faturada disponível pra incluir.</div>}
          <table className="w-full text-sm">
            <tbody>
              {lista.map(p => (
                <tr key={p.pedido_id} className={`border-b border-ink-50 ${sel.has(p.pedido_id) ? 'bg-amber-50' : ''}`}>
                  <td className="px-2 py-1.5 w-8"><input type="checkbox" checked={sel.has(p.pedido_id)} onChange={() => toggle(p.pedido_id)} /></td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {p.nf_numero ? <span className="font-medium text-cyan-700">NF {p.nf_numero}</span> : <span className="text-ink-500">Pedido #{p.numero}</span>}
                    <span className="text-[11px] text-ink-400 ml-1">#{p.numero}</span>
                  </td>
                  <td className="px-2 py-1.5 truncate max-w-[200px]">
                    {p.cliente || '—'}
                    {p.cidade && <span className="text-[11px] text-ink-400 ml-1">· {p.cidade}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {p.uf && <span className="text-[11px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 font-medium">{p.uf}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{formatMoney(p.valor)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {p.carga_atual
                      ? <span className="text-[11px] text-amber-700 inline-flex items-center gap-0.5" title={`Está na carga #${p.carga_atual.numero} (${p.carga_atual.status}) — será transferida`}><ArrowRightLeft size={11} /> da #{p.carga_atual.numero}</span>
                      : <span className="text-[11px] text-emerald-600">solta</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t flex justify-between items-center">
          <span className="text-xs text-ink-500">{sel.size} selecionada(s)</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 text-sm font-medium">Cancelar</button>
            <button disabled={sel.size === 0 || incluir.isPending} onClick={() => incluir.mutate()}
              className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
              {incluir.isPending && <Loader2 size={14} className="animate-spin" />} Incluir {sel.size > 0 ? sel.size : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Acompanhamento do faturamento da carga, pedido-a-pedido (job em background no backend).
// Faz polling em /faturar-progresso enquanto running; para quando termina.
export function ModalProgressoFaturamento({ cargaId, onClose }: { cargaId: number; onClose: () => void }) {
  const { data } = useQuery<any>({
    queryKey: ['fatura-progresso', cargaId],
    queryFn: async () => (await api.get(`/vendas/cargas/${cargaId}/faturar-progresso`)).data,
    refetchInterval: (q: any) => (q.state.data?.running ? 1500 : false),
    refetchIntervalInBackground: true,
  })
  const existe = data?.existe
  const running = !!data?.running
  const total = data?.total || 0
  const done = data?.done || 0
  const ok = data?.ok || 0
  const falhas = data?.falhas || 0
  const itens: any[] = data?.itens || []
  const pct = total ? Math.round((done / total) * 100) : 0

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2 font-semibold text-ink-800">
            {running ? <Loader2 size={18} className="animate-spin text-emerald-600" /> : <CheckCircle2 size={18} className="text-emerald-600" />}
            Faturamento — Carga #{cargaId}
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={20} /></button>
        </div>

        {!existe ? (
          <div className="p-8 text-center text-ink-500 text-sm">Nenhum faturamento em andamento para esta carga.</div>
        ) : (
          <>
            <div className="px-5 pt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-ink-700">{running ? 'Faturando…' : 'Concluído'}</span>
                <span className="text-ink-500">{done}/{total}</span>
              </div>
              <div className="h-2.5 rounded-full bg-ink-100 overflow-hidden">
                <div className={`h-full transition-all ${falhas ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex gap-3 mt-2 text-xs">
                <span className="text-emerald-700 font-medium">✓ {ok} ok</span>
                {falhas > 0 && <span className="text-rose-700 font-medium">✗ {falhas} falha(s)</span>}
                <span className="text-ink-400">de {total}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3 mt-1">
              <table className="w-full text-sm">
                <tbody>
                  {itens.map((it) => (
                    <tr key={it.pedido_id} className="border-b border-ink-50">
                      <td className="py-1.5 px-2 w-6">
                        {it.status === 'ok' ? <CheckCircle2 size={16} className="text-emerald-500" />
                          : it.status === 'erro' ? <AlertCircle size={16} className="text-rose-500" />
                          : it.status === 'processando' ? <Loader2 size={16} className="animate-spin text-emerald-600" />
                          : <Clock size={16} className="text-ink-300" />}
                      </td>
                      <td className="py-1.5 px-2 font-medium text-ink-700 whitespace-nowrap">#{it.numero}</td>
                      <td className="py-1.5 px-2 text-ink-600 truncate max-w-[220px]">{it.cliente}</td>
                      <td className="py-1.5 px-2">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded ${it.tipo === 'fiscal' ? 'bg-cyan-100 text-cyan-800' : 'bg-ink-100 text-ink-600'}`}>{it.tipo === 'fiscal' ? 'NF' : 'simples'}</span>
                      </td>
                      <td className="py-1.5 px-2 text-right text-xs">
                        {it.status === 'ok' && it.nf_numero ? <span className="text-emerald-700">NF {it.nf_numero}</span>
                          : it.status === 'ok' ? <span className="text-emerald-700">ok</span>
                          : it.status === 'erro' ? <span className="text-rose-600" title={it.motivo}>{(it.motivo || 'erro').slice(0, 40)}</span>
                          : it.status === 'processando' ? <span className="text-emerald-600">processando…</span>
                          : <span className="text-ink-300">aguardando</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t flex justify-between items-center">
              <span className="text-xs text-ink-400">{running ? 'Pode fechar — continua rodando no servidor.' : 'Faturamento finalizado.'}</span>
              <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 text-sm font-medium">Fechar</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function ModalFaturar({ carga, faturando, onClose, onFaturar }: { carga: any; faturando: boolean; onClose: () => void; onFaturar: (ids: number[], motoristaId: number | null, veiculoId: number | null, bancoBoletoId: number | null) => void }) {
  const { data: resumo, isLoading } = useQuery<any>({
    queryKey: ['carga-resumo-fat', carga.id],
    queryFn: async () => (await api.get(`/vendas/cargas/${carga.id}/resumo`)).data,
  })
  const { data: veiculos = [] } = useQuery<any[]>({ queryKey: ['exp-veiculos'], queryFn: async () => (await api.get('/expedicao/veiculos')).data })
  const { data: pesoData } = useQuery<any>({ queryKey: ['carga-peso', carga.id], queryFn: async () => (await api.get(`/vendas/cargas/${carga.id}/peso`)).data })
  const { data: motoristas = [] } = useQuery<any[]>({ queryKey: ['motoristas'], queryFn: async () => (await api.get('/expedicao/motoristas')).data })
  const { data: bancos = [] } = useQuery<any[]>({ queryKey: ['bancos-boleto'], queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data })
  const bancosBoleto = bancos.filter((b: any) => b.emite_boleto)
  const paradas = resumo?.paradas || []
  const [semNota, setSemNota] = useState<Set<number>>(new Set())
  const [motoristaId, setMotoristaId] = useState<string>(carga.motorista_id ? String(carga.motorista_id) : '')
  const [veiculoId, setVeiculoId] = useState<string>(carga.veiculo_id ? String(carga.veiculo_id) : '')
  const [bancoBoletoId, setBancoBoletoId] = useState<string>('')
  // Há pedido com boleto na carga? então o banco é obrigatório pra gerar os boletos
  const temBoleto = paradas.some((p: any) => p.forma_prevista === 'boleto')
  // Pré-marca "sem nota" os pedidos que já foram digitados como simples (tipo_venda).
  // Roda uma vez quando o resumo chega; toggles manuais depois não são sobrescritos.
  const preMarcou = useRef(false)
  useEffect(() => {
    if (preMarcou.current || !resumo?.paradas) return
    preMarcou.current = true
    const simples = resumo.paradas.filter((p: any) => p.tipo_venda === 'simples').map((p: any) => p.pedido_id)
    if (simples.length) setSemNota(new Set(simples))
  }, [resumo])
  const toggle = (pid: number) => setSemNota(s => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n })
  const comNota = paradas.length - semNota.size
  // Validação de peso da carga vs veículo selecionado (+ tolerância configurável no veículo)
  const veic = veiculos.find((v: any) => String(v.id) === veiculoId)
  const pesoCarga = pesoData?.peso_total_kg ?? null
  const cap = veic ? Number(veic.capacidade_kg || 0) : 0
  const tol = veic ? Number(veic.tolerancia_peso_pct ?? 5) : 5
  const limitePeso = cap > 0 ? Math.round(cap * (1 + tol / 100)) : 0
  const excedePeso = !!(veic && cap > 0 && pesoCarga != null && pesoCarga > limitePeso)
  const cubCarga = pesoData?.cubagem_total_m3 ?? null
  const cubCap = veic ? Number(veic.cubagem_m3 || 0) : 0
  const limiteCub = cubCap > 0 ? +(cubCap * (1 + tol / 100)).toFixed(2) : 0
  const excedeCub = !!(veic && cubCap > 0 && cubCarga != null && cubCarga > limiteCub)
  const excede = excedePeso || excedeCub
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold flex items-center gap-2"><Receipt size={16} /> Faturar carga #{carga.numero ?? carga.id}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium text-ink-500 mb-1">Motorista</div>
              <select value={motoristaId} onChange={(e) => setMotoristaId(e.target.value)} className="input w-full text-sm">
                <option value="">Sem motorista</option>
                {motoristas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium text-ink-500 mb-1">Veículo</div>
              <select value={veiculoId} onChange={(e) => setVeiculoId(e.target.value)} className="input w-full text-sm">
                <option value="">Sem veículo</option>
                {veiculos.map((v) => <option key={v.id} value={v.id}>{v.placa}{(v.tipo_carroceria || v.observacoes) ? ` — ${v.tipo_carroceria || v.observacoes}` : ''}{v.capacidade_kg ? ` (${v.capacidade_kg} kg)` : ''}</option>)}
              </select>
            </label>
          </div>
          {veic && (cap > 0 || cubCap > 0) && pesoCarga != null && (
            <div className={`text-sm rounded-lg px-3 py-2 border space-y-1 ${excede ? 'bg-red-50 border-red-300 text-red-700' : 'bg-emerald-50 border-emerald-300 text-emerald-700'}`}>
              {cap > 0 && (
                <div>{excedePeso ? '🚛' : '✅'} <b>Peso:</b> carga {Math.round(pesoCarga)} kg / {veic.placa} {cap} kg (+{tol}% = {limitePeso} kg){excedePeso && <> — <b>excede {Math.round(pesoCarga - cap)} kg</b></>}</div>
              )}
              {cubCap > 0 && cubCarga != null && (
                <div>{excedeCub ? '📦' : '✅'} <b>Cubagem:</b> carga {cubCarga.toFixed(2)} m³ / {veic.placa} {cubCap} m³ (+{tol}% = {limiteCub} m³){excedeCub && <> — <b>excede {(cubCarga - cubCap).toFixed(2)} m³</b></>}</div>
              )}
              {excede && <div className="font-medium">Não dá pra faturar nesse veículo — escolha outro, reduza a carga ou aumente a tolerância no cadastro do veículo.</div>}
              {(pesoData?.sem_peso > 0 || pesoData?.sem_cubagem > 0) && (
                <div className="text-xs text-amber-600">⚠ {pesoData.sem_peso} itens sem peso{pesoData?.sem_cubagem ? ` · ${pesoData.sem_cubagem} sem cubagem` : ''} — totais podem estar subestimados.</div>
              )}
            </div>
          )}
          {temBoleto && (
            <label className="block">
              <div className="text-xs font-medium text-ink-500 mb-1">🏦 Banco do boleto * <span className="text-ink-400 font-normal">(há pedidos com boleto na carga)</span></div>
              <select value={bancoBoletoId} onChange={(e) => setBancoBoletoId(e.target.value)} className="input w-full text-sm">
                <option value="">Selecione o banco emissor…</option>
                {bancosBoleto.map((b: any) => <option key={b.id} value={b.id}>{b.nome}</option>)}
              </select>
            </label>
          )}
          <p className="text-xs text-ink-500">Marque os pedidos que vão <strong>SEM nota</strong> (venda simples). Os demais saem <strong>com NF-e</strong>. A forma de pagamento vem do pedido. Todos baixam estoque e geram a receber.</p>
          <div className="flex gap-3 text-sm">
            <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-700">Com nota: {comNota}</span>
            <span className="px-2 py-1 rounded bg-slate-200 text-slate-700">Sem nota: {semNota.size}</span>
            <button className="text-xs text-brand-700 underline" onClick={() => setSemNota(new Set(paradas.map((p: any) => p.pedido_id)))}>marcar todos sem nota</button>
            <button className="text-xs text-brand-700 underline" onClick={() => setSemNota(new Set())}>todos com nota</button>
          </div>
          {isLoading ? <div className="text-ink-400 py-6 text-center">Carregando...</div> : (
            <div className="border rounded divide-y">
              {paradas.map((p: any) => {
                const sn = semNota.has(p.pedido_id)
                return (
                  <div key={p.pedido_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <span className="font-medium">Ped #{p.pedido_numero}</span> · {p.cliente || `Cliente`}
                      <span className="text-ink-400 ml-2">{formatMoney(p.valor_previsto)}</span>
                    </div>
                    <button onClick={() => toggle(p.pedido_id)}
                      className={`text-xs px-2 py-1 rounded font-medium ${sn ? 'bg-slate-200 text-slate-700' : 'bg-indigo-100 text-indigo-700'}`}>
                      {sn ? 'SEM nota (simples)' : 'COM nota (NF-e)'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div className="border-t px-5 py-3 flex justify-end gap-2 bg-ink-50 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button disabled={faturando || paradas.length === 0 || (temBoleto && !bancoBoletoId) || excede} onClick={() => onFaturar(Array.from(semNota), motoristaId ? Number(motoristaId) : null, veiculoId ? Number(veiculoId) : null, bancoBoletoId ? Number(bancoBoletoId) : null)}
            className="btn-primary bg-emerald-600 hover:bg-emerald-700 border-emerald-600">
            {faturando ? <Loader2 className="animate-spin" size={14} /> : <Receipt size={14} />}
            {faturando ? 'Faturando…' : `Faturar (${comNota} c/ NF + ${semNota.size} simples)`}
          </button>
        </div>
      </div>
    </div>
  )
}

function ModalAcerto({ carga, onClose, onDone }: { carga: any; onClose: () => void; onDone: () => void }) {
  const { data: resumo, isLoading } = useQuery<any>({
    queryKey: ['carga-resumo', carga.id],
    queryFn: async () => (await api.get(`/vendas/cargas/${carga.id}/resumo`)).data,
  })
  const { data: contasBanco = [] } = useQuery<any[]>({
    queryKey: ['contas-banco-acerto'],
    queryFn: async () => (await api.get('/financeiro/contas-bancarias')).data,
  })
  // Contas gerenciais analíticas (pra classificar a despesa da carga)
  const { data: cgsAll = [] } = useQuery<any[]>({
    queryKey: ['cg-analiticas-acerto'],
    queryFn: async () => (await api.get('/plano-contas/analiticas')).data,
  })
  const cgsDespesa = cgsAll.filter((c: any) => String(c.tipo || '').startsWith('despesa') || c.tipo === 'custo')
  const { data: motivosDev = [] } = useQuery<any[]>({
    queryKey: ['motivos-devolucao-acerto'],
    queryFn: async () => (await api.get('/cadastros/motivos-devolucao', { params: { apenas_ativos: true } })).data,
  })
  // estado por parada: { entregue, recebimentos: [{forma, valor, banco, numero, bom_para, data}] }
  const hojeISO = new Date().toISOString().slice(0, 10)
  const [estado, setEstado] = useState<Record<number, any>>({})
  const [despesas, setDespesas] = useState<{ descricao: string; valor: string; conta_gerencial_id: string; forma: string; data: string; conta_id: string }[]>([])

  // Seed da parada: se o motorista registrou no app, usa o que ELE recebeu
  // (forma/valor/banco) — "já chega real no acerto". Senão, usa o previsto.
  // Mapeia a forma prevista do pedido pra forma inicial do recebimento no acerto.
  const segForma = (fp?: string) => {
    if (fp === 'prazo' || fp === 'a_prazo') return 'a_prazo'
    // Bonificação/troca ("é dado"): NÃO pode virar dinheiro automático — senão a tela
    // semeia um recebimento de dinheiro fantasma com o valor previsto e o fechamento
    // cria receita de caixa + despesa de bonificação no mesmo pedido (sobra falsa).
    if (fp === 'bonificacao' || fp === 'bonificação' || fp === 'troca') return 'bonificacao'
    if (fp && ['dinheiro', 'pix', 'cheque', 'boleto'].includes(fp)) return fp
    return 'dinheiro'
  }
  const seedParada = (p: any) => {
    const m = p.motorista
    const baseDev = { tipo_devolucao: 'DEVT', destino_financeiro: 'credito', motivo_devolucao_id: '', itens_dev: {} as Record<number, string> }
    // Pago "por fora" antes do acerto (PIX direto, baixa manual do financeiro):
    // vem entregue=true SEM recebimentos. Backend só marca entregue e contabiliza.
    if (p.ja_pago_externo) {
      return { entregue: true, recebimentos: [], sobra: '', falta: '', desconto: '', ...baseDev }
    }
    if (m && (m.recebimentos?.length || m.status === 'nao_entregue')) {
      return {
        entregue: m.status === 'entregue',
        recebimentos: (m.recebimentos || []).map((r: any) => ({
          forma: r.forma, valor: String(r.valor ?? ''), conta_id: String(r.conta_id || ''),
          banco: r.cheque_banco || '', numero: r.cheque_numero || '', bom_para: r.cheque_bom_para || '',
          data: (r.data || '').slice(0, 10) || hojeISO,
        })),
        sobra: '', falta: '', desconto: '', ...baseDev,
      }
    }
    // devolvido/cancelado (re-acerto) NÃO nasce como entregue → não infla o previsto
    const entregueDefault = p.entregue !== false && !['devolvido', 'cancelado'].includes(p.status_pedido)
    return {
      entregue: entregueDefault,
      recebimentos: [{ forma: segForma(p.forma_prevista), valor: String(p.valor_previsto || ''), banco: '', numero: '', bom_para: '', conta_id: '', data: hojeISO }],
      sobra: '', falta: '', desconto: '', ...baseDev,
    }
  }
  const getP = (p: any) => estado[p.pedido_id] || seedParada(p)
  const setP = (p: any, patch: any) => setEstado((s) => ({ ...s, [p.pedido_id]: { ...getP(p), ...s[p.pedido_id], ...patch } }))
  const setRec = (p: any, idx: number, patch: any) => {
    const e = { ...getP(p), ...estado[p.pedido_id] }
    const recs = [...e.recebimentos]; recs[idx] = { ...recs[idx], ...patch }
    setEstado((s) => ({ ...s, [p.pedido_id]: { ...e, recebimentos: recs } }))
  }
  const addRec = (p: any) => {
    const e = { ...getP(p), ...estado[p.pedido_id] }
    setEstado((s) => ({ ...s, [p.pedido_id]: { ...e, recebimentos: [...e.recebimentos, { forma: 'dinheiro', valor: '', banco: '', numero: '', bom_para: '', conta_id: '', data: hojeISO }] } }))
  }
  const delRec = (p: any, idx: number) => {
    const e = { ...getP(p), ...estado[p.pedido_id] }
    setEstado((s) => ({ ...s, [p.pedido_id]: { ...e, recebimentos: e.recebimentos.filter((_: any, i: number) => i !== idx) } }))
  }
  const somaP = (p: any) => (getP(p).recebimentos || []).reduce((s: number, r: any) => s + (Number(r.valor) || 0), 0)
  const okP = (p: any) => {
    const e = getP(p)
    if (!e.entregue) return true
    // Pago por fora: já está conferido (dinheiro caiu no banco antes do acerto).
    if (p.ja_pago_externo) return true
    if (somaP(p) <= 0) return false
    // Cheque com valor exige banco, número e bom para; PIX exige a data do pagamento
    return (e.recebimentos || []).every((r: any) => {
      if (!(Number(r.valor) > 0)) return true
      if (r.forma === 'cheque') return r.banco && r.numero && r.bom_para
      if (r.forma === 'pix') return !!r.data
      return true
    })
  }

  const paradas = resumo?.paradas || []
  const allOk = paradas.length > 0 && paradas.every((p: any) => okP(p))
  const recebido = paradas.reduce((s: number, p: any) => {
    if (!getP(p).entregue) return s
    // Pago externo: o valor já caiu na conta antes do acerto, contabiliza no total.
    if (p.ja_pago_externo) return s + (Number(p.valor_recebido) || 0)
    return s + somaP(p)
  }, 0)
  const previstoEntregue = paradas.reduce((s: number, p: any) => s + (getP(p).entregue ? (p.valor_previsto || 0) : 0), 0)
  const recebidoExterno = paradas.reduce((s: number, p: any) =>
    s + ((getP(p).entregue && p.ja_pago_externo) ? (Number(p.valor_recebido) || 0) : 0), 0)
  const totalSobra = paradas.reduce((s: number, p: any) => s + (getP(p).entregue ? (Number(getP(p).sobra) || 0) : 0), 0)
  const totalFalta = paradas.reduce((s: number, p: any) => s + (getP(p).entregue ? (Number(getP(p).falta) || 0) : 0), 0)
  const totalDesconto = paradas.reduce((s: number, p: any) => s + (getP(p).entregue ? (Number(getP(p).desconto) || 0) : 0), 0)
  const diferenca = totalSobra - totalFalta - totalDesconto
  const totalDespesas = despesas.reduce((s, d) => s + (Number(d.valor) || 0), 0)

  const acertar = useMutation({
    mutationFn: async () => {
      // PIX na despesa exige a data (é a data que lança no banco)
      const pixSemData = despesas.find((d) => Number(d.valor) > 0 && d.descricao && d.forma === 'pix' && !d.data)
      if (pixSemData) {
        toast.error(`Despesa PIX "${pixSemData.descricao}": informe a data do PIX.`)
        throw new Error('pix sem data')
      }
      // PIX recebido exige a data do pagamento (dia em que o PIX caiu → vai no movimento)
      const pixRecSemData = paradas.find((p: any) => {
        const e = getP(p)
        return e.entregue && (e.recebimentos || []).some((r: any) => r.forma === 'pix' && Number(r.valor) > 0 && !r.data)
      })
      if (pixRecSemData) {
        toast.error(`Parada ${pixRecSemData.sequencia} (${pixRecSemData.cliente || '#' + pixRecSemData.pedido_numero}): informe a data do PIX.`)
        throw new Error('pix recebimento sem data')
      }
      // Devolução exige motivo (cadastro)
      const devSemMotivo = paradas.find((p: any) => !getP(p).entregue && !getP(p).motivo_devolucao_id)
      if (devSemMotivo) {
        toast.error(`Pedido #${devSemMotivo.numero || devSemMotivo.pedido_id}: selecione o motivo da devolução.`)
        throw new Error('devolução sem motivo')
      }
      const itens = paradas.map((p: any) => {
        const e = getP(p)
        const dev = !e.entregue && (e.tipo_devolucao === 'DEVT' || e.tipo_devolucao === 'DEVP')
        const itensDevPayload = dev && e.tipo_devolucao === 'DEVP'
          ? (p.itens || [])
              .map((it: any) => ({ pedido_item_id: it.id, quantidade: Number(e.itens_dev?.[it.id] || 0) }))
              .filter((x: any) => x.quantidade > 0)
          : []
        return {
          pedido_id: p.pedido_id,
          entregue: e.entregue,
          recebimentos: e.entregue ? (e.recebimentos || []).filter((r: any) => Number(r.valor) > 0).map((r: any) => ({
            forma: r.forma, valor: Number(r.valor),
            conta_id: (r.forma === 'pix' || r.forma === 'dinheiro') && r.conta_id ? Number(r.conta_id) : null,
            data: (r.forma === 'pix' || r.forma === 'dinheiro') ? (r.data || null) : null,
            cheque_banco: r.forma === 'cheque' ? r.banco : null,
            cheque_numero: r.forma === 'cheque' ? r.numero : null,
            cheque_bom_para: r.forma === 'cheque' ? (r.bom_para || null) : null,
          })) : [],
          sobra: e.entregue ? (Number(e.sobra) || 0) : 0,
          falta: e.entregue ? (Number(e.falta) || 0) : 0,
          desconto: e.entregue ? (Number(e.desconto) || 0) : 0,
          tipo_devolucao: dev ? e.tipo_devolucao : null,
          itens_devolucao: itensDevPayload,
          destino_financeiro: dev ? (e.destino_financeiro || 'credito') : 'credito',
          motivo_devolucao_id: !e.entregue && e.motivo_devolucao_id ? Number(e.motivo_devolucao_id) : null,
        }
      })
      const desp = despesas.filter((d) => Number(d.valor) > 0 && d.descricao).map((d) => ({
        descricao: d.descricao, valor: Number(d.valor),
        conta_gerencial_id: d.conta_gerencial_id ? Number(d.conta_gerencial_id) : null,
        conta_id: d.conta_id ? Number(d.conta_id) : null,
        forma: d.forma || null,
        data: d.forma === 'pix' ? (d.data || null) : null,
      }))
      return (await api.post(`/vendas/cargas/${carga.id}/acertar`, { itens, despesas: desp })).data
    },
    onSuccess: (d: any) => {
      const r = d.resumo || {}
      toast.success(`Carga fechada: ${r.entregues} entregue(s), ${r.nao_entregues} devolvido(s) · dif ${formatMoney(d.diferenca || 0)}`, { duration: 7000 })
      if ((d.fiscal_pendente || []).length) toast.error(`NF a cancelar/devolver: ${(d.fiscal_pendente || []).join(', ')}`, { duration: 8000 })
      onDone()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no acerto'),
  })

  // ===== RASCUNHO: recarrega o que já foi feito ao reabrir (só carga em aberto) =====
  const qcAcerto = useQueryClient()
  const fechada = (resumo?.carga?.status || carga.status) === 'fechada'
  const [rascReady, setRascReady] = useState(false)
  useEffect(() => {
    if (!resumo || rascReady) return
    const est = resumo?.rascunho?.estado
    if (est && Object.keys(est).length) {
      setEstado(est)
      if (Array.isArray(resumo.rascunho.despesas)) setDespesas(resumo.rascunho.despesas)
    }
    setRascReady(true)
  }, [resumo, rascReady])

  // monta o rascunho com o estado EFETIVO de todas as paradas (inclui as ainda no seed)
  const montarRascunho = () => ({
    estado: Object.fromEntries((resumo?.paradas || []).map((p: any) => [p.pedido_id, getP(p)])),
    despesas,
    conferidas: (resumo?.paradas || []).filter((p: any) => okP(p)).length,
    total: (resumo?.paradas || []).length,
  })
  const salvarRascunho = useMutation({
    mutationFn: async () => (await api.put(`/vendas/cargas/${carga.id}/acerto-rascunho`, montarRascunho())).data,
    onSuccess: (d: any) => { toast.success(`Rascunho salvo — ${d.conferidas}/${d.total} conferidas`); qcAcerto.invalidateQueries({ queryKey: ['cargas-dist'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar rascunho'),
  })
  const imprimirConferencia = async () => {
    try {
      await salvarRascunho.mutateAsync()   // salva antes (o PDF lê do rascunho)
      const r = await api.get(`/vendas/cargas/${carga.id}/acerto-conferencia-pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { /* erro já tratado no toast */ }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
          <h2 className="font-bold flex items-center gap-2"><ClipboardCheck size={18} /> Acerto da carga #{carga.numero}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          {isLoading || !resumo ? <div className="text-center text-ink-500 py-6">Carregando…</div> : (
            <>
              {paradas.map((p: any) => {
                const e = getP(p); const ok = okP(p); const soma = somaP(p)
                return (
                  <div key={p.pedido_id} className={`border-2 rounded-lg p-3 space-y-2 ${ok ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {ok && <CheckCircle2 size={15} className="text-emerald-600" />}
                        Parada {p.sequencia} · {p.cliente || `Ped #${p.pedido_numero}`} · {formatMoney(p.valor_previsto)}
                        <span className="text-xs text-ink-400">(prev: {p.forma_prevista || '—'})</span>
                      </div>
                      <label className="flex items-center gap-1 text-sm">
                        <input type="checkbox" checked={e.entregue} onChange={(ev) => setP(p, { entregue: ev.target.checked })} /> Entregue
                      </label>
                    </div>
                    {p.motorista && (
                      <div className={`text-xs rounded-md px-2 py-1 flex flex-wrap items-center gap-2 ${p.motorista.status === 'entregue' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                        <span className="font-semibold">📱 Motorista:</span>
                        {p.motorista.status === 'entregue'
                          ? <span>recebeu {formatMoney(p.motorista.valor_recebido)} ({(p.motorista.recebimentos || []).map((r: any) => r.forma).join(', ') || '—'})</span>
                          : <span>NÃO entregue{p.motorista.motivo_nao_entrega ? ` — ${p.motorista.motivo_nao_entrega}` : ''}</span>}
                        {p.motorista.observacao && <span className="italic">· {p.motorista.observacao}</span>}
                        {p.motorista.foto_url && <a href={p.motorista.foto_url} target="_blank" rel="noreferrer" className="underline">ver foto</a>}
                      </div>
                    )}
                    {e.entregue && p.ja_pago_externo ? (
                      <div className="rounded-md bg-emerald-100 border border-emerald-300 px-3 py-2 text-emerald-900 text-sm flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-emerald-700 shrink-0" />
                        <div className="flex-1">
                          <div className="font-medium">Título já baixado pelo financeiro</div>
                          <div className="text-xs text-emerald-800/80">
                            R$ {formatMoney(p.valor_recebido).replace('R$', '').trim()}
                            {p.forma_recebida ? ` · ${p.forma_recebida.toUpperCase()}` : ''}
                            {p.pago_em ? ` · em ${new Date(p.pago_em).toLocaleDateString('pt-BR')}` : ''}
                            {' '}— abatido do total da carga, sem ação no acerto.
                          </div>
                        </div>
                      </div>
                    ) : e.entregue ? (
                      <div className="space-y-1.5">
                        {e.recebimentos.map((r: any, idx: number) => (
                          <div key={idx} className="flex flex-wrap items-center gap-2">
                            <select value={r.forma} onChange={(ev) => setRec(p, idx, { forma: ev.target.value })} className="input !py-1 text-sm">
                              <option value="dinheiro">Dinheiro</option>
                              <option value="pix">Pix</option>
                              <option value="pix_pendente">PIX pendente (fica a receber)</option>
                              <option value="cheque">CH - Cheque</option>
                              <option value="boleto">Boleto</option>
                              <option value="a_prazo">A Prazo (fica a receber)</option>
                              <option value="bonificacao">Bonificação</option>
                            </select>
                            <input type="number" step="0.01" value={r.valor} onChange={(ev) => setRec(p, idx, { valor: ev.target.value })} className="input !py-1 text-sm w-28" placeholder="Valor" />
                            {(r.forma === 'pix' || r.forma === 'dinheiro') && (
                              <select value={r.conta_id || ''} onChange={(ev) => setRec(p, idx, { conta_id: ev.target.value })}
                                className="input !py-1 text-sm w-44" title={r.forma === 'pix' ? 'Banco que recebeu o pix' : 'Conta/caixa'}>
                                <option value="">{r.forma === 'pix' ? 'Qual banco recebeu?' : 'Conta (caixa)'}</option>
                                {contasBanco.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                              </select>
                            )}
                            {(r.forma === 'pix' || r.forma === 'dinheiro') && (
                              <input type="date" value={r.data || ''} onChange={(ev) => setRec(p, idx, { data: ev.target.value })}
                                className={`input !py-1 text-sm w-40 ${r.forma === 'pix' && Number(r.valor) > 0 && !r.data ? 'border-amber-400 bg-amber-50/40' : ''}`}
                                title={r.forma === 'pix' ? 'Data do PIX (dia que caiu) *' : 'Data do recebimento'} />
                            )}
                            {r.forma === 'cheque' && (
                              <>
                                <input value={r.banco} onChange={(ev) => setRec(p, idx, { banco: ev.target.value })} placeholder="Banco *" className={`input !py-1 text-sm w-28 ${Number(r.valor) > 0 && !r.banco ? 'border-amber-400' : ''}`} />
                                <input value={r.numero} onChange={(ev) => setRec(p, idx, { numero: ev.target.value })} placeholder="Nº *" className={`input !py-1 text-sm w-20 ${Number(r.valor) > 0 && !r.numero ? 'border-amber-400' : ''}`} />
                                <input type="date" value={r.bom_para} onChange={(ev) => setRec(p, idx, { bom_para: ev.target.value })} className={`input !py-1 text-sm ${Number(r.valor) > 0 && !r.bom_para ? 'border-amber-400' : ''}`} title="Bom para *" />
                              </>
                            )}
                            {e.recebimentos.length > 1 && <button onClick={() => delRec(p, idx)} className="text-red-600"><X size={14} /></button>}
                          </div>
                        ))}
                        <div className="flex items-center gap-3 text-xs">
                          <button onClick={() => addRec(p)} className="flex items-center gap-1 text-brand-700"><Plus size={13} /> desdobrar (outra forma)</button>
                          <span className={`${Math.abs(soma - p.valor_previsto) < 0.005 ? 'text-emerald-700' : 'text-amber-700'}`}>recebido: {formatMoney(soma)}{Math.abs(soma - p.valor_previsto) >= 0.005 ? ` (prev ${formatMoney(p.valor_previsto)})` : ''}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs pt-1 border-t border-ink-100">
                          <label className="flex items-center gap-1 text-emerald-700">
                            Sobra R$
                            <input type="number" step="0.01" min="0" value={e.sobra}
                              onChange={(ev) => setP(p, { sobra: ev.target.value })}
                              className="input !py-0.5 !px-1.5 text-xs w-20" placeholder="0,00" />
                          </label>
                          <label className="flex items-center gap-1 text-rose-700">
                            Falta R$
                            <input type="number" step="0.01" min="0" value={e.falta}
                              onChange={(ev) => setP(p, { falta: ev.target.value })}
                              className="input !py-0.5 !px-1.5 text-xs w-20" placeholder="0,00" />
                          </label>
                          <label className="flex items-center gap-1 text-amber-700">
                            Desconto R$
                            <input type="number" step="0.01" min="0" value={e.desconto}
                              onChange={(ev) => setP(p, { desconto: ev.target.value })}
                              className="input !py-0.5 !px-1.5 text-xs w-20" placeholder="0,00" />
                          </label>
                          <span className="text-ink-400">→ DRE (conta gerencial)</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 text-xs">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="font-medium text-rose-700">Devolução:</span>
                          <label className="flex items-center gap-1">
                            <input type="radio" name={`tipo-${p.pedido_id}`} value="DEVT"
                              checked={e.tipo_devolucao === 'DEVT'}
                              onChange={() => setP(p, { tipo_devolucao: 'DEVT' })} />
                            <span>Total (DEVT)</span>
                          </label>
                          <label className="flex items-center gap-1">
                            <input type="radio" name={`tipo-${p.pedido_id}`} value="DEVP"
                              checked={e.tipo_devolucao === 'DEVP'}
                              onChange={() => setP(p, { tipo_devolucao: 'DEVP' })} />
                            <span>Parcial (DEVP)</span>
                          </label>
                          <span className="text-ink-400">|</span>
                          <span className="font-medium">Valor pago retorna como:</span>
                          <label className="flex items-center gap-1">
                            <input type="radio" name={`dest-${p.pedido_id}`} value="credito"
                              checked={(e.destino_financeiro || 'credito') === 'credito'}
                              onChange={() => setP(p, { destino_financeiro: 'credito' })} />
                            <span>Crédito do cliente</span>
                          </label>
                          <label className="flex items-center gap-1">
                            <input type="radio" name={`dest-${p.pedido_id}`} value="abater"
                              checked={e.destino_financeiro === 'abater'}
                              onChange={() => setP(p, { destino_financeiro: 'abater' })} />
                            <span>Restituir em $</span>
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-rose-700">Motivo *</span>
                          <select value={e.motivo_devolucao_id || ''}
                            onChange={(ev) => setP(p, { motivo_devolucao_id: ev.target.value })}
                            className={`input !py-1 text-sm flex-1 ${!e.motivo_devolucao_id ? 'border-amber-400 bg-amber-50/40' : ''}`}>
                            <option value="">Selecione o motivo da devolução…</option>
                            {motivosDev.map((m: any) => <option key={m.id} value={m.id}>{m.nome}</option>)}
                          </select>
                          {motivosDev.length === 0 && (
                            <span className="text-[11px] text-amber-700">cadastre em Cadastros › Motivos de devolução</span>
                          )}
                        </div>
                        {e.tipo_devolucao === 'DEVP' && (
                          <div className="bg-rose-50/40 border border-rose-200 rounded p-2 space-y-1">
                            <div className="text-rose-700 font-medium">Itens devolvidos (qtd):</div>
                            {(p.itens || []).map((it: any) => (
                              <div key={it.id} className="flex items-center gap-2">
                                <span className="flex-1 truncate" title={it.descricao}>{it.descricao}</span>
                                <span className="text-ink-400 text-[11px]">venda: {it.quantidade}</span>
                                <input type="number" step="0.001" min="0" max={it.quantidade}
                                  value={e.itens_dev?.[it.id] ?? ''}
                                  onChange={(ev) => setP(p, { itens_dev: { ...(e.itens_dev || {}), [it.id]: ev.target.value } })}
                                  placeholder="0"
                                  className="input !py-0.5 !px-1.5 text-xs w-20" />
                              </div>
                            ))}
                            {(p.itens || []).length === 0 && <div className="text-ink-400 italic">Pedido sem itens registrados.</div>}
                          </div>
                        )}
                        <div className="text-ink-500 italic">
                          {e.tipo_devolucao === 'DEVT'
                            ? '→ Gera NF de entrada (devolução total), devolve estoque, cancela contas em aberto.'
                            : '→ Gera NF de entrada parcial (só dos itens devolvidos), devolve estoque das qtds, abate proporcional no financeiro.'}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Despesas da carga */}
              <div className="border rounded-lg p-3 space-y-2">
                <div className="text-xs font-semibold text-ink-500 uppercase">Despesas da carga (combustível, comissão…)</div>
                {despesas.map((d, i) => {
                  const upd = (patch: any) => setDespesas((s) => s.map((x, j) => j === i ? { ...x, ...patch } : x))
                  return (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <input value={d.descricao} onChange={(e) => upd({ descricao: e.target.value })} placeholder="Descrição" className="input !py-1 text-sm flex-1 min-w-[140px]" />
                    <input type="number" step="0.01" value={d.valor} onChange={(e) => upd({ valor: e.target.value })} placeholder="Valor" className="input !py-1 text-sm w-24" />
                    <select value={d.conta_gerencial_id} onChange={(e) => upd({ conta_gerencial_id: e.target.value })}
                      className={`input !py-1 text-sm w-44 ${!d.conta_gerencial_id ? 'border-amber-400 bg-amber-50/40' : ''}`}>
                      <option value="">Conta gerencial…</option>
                      {cgsDespesa.map((c: any) => <option key={c.id} value={c.id}>{c.codigo} — {c.nome}</option>)}
                    </select>
                    <select value={d.forma} onChange={(e) => upd({ forma: e.target.value, data: e.target.value === 'pix' ? d.data : '' })}
                      className="input !py-1 text-sm w-28">
                      <option value="dinheiro">Dinheiro</option>
                      <option value="pix">PIX</option>
                    </select>
                    {d.forma === 'pix' && (
                      <input type="date" value={d.data} onChange={(e) => upd({ data: e.target.value })}
                        title="Data do PIX (lança no banco nessa data)"
                        className={`input !py-1 text-sm w-36 ${!d.data ? 'border-amber-400 bg-amber-50/40' : ''}`} />
                    )}
                    <select value={d.conta_id} onChange={(e) => upd({ conta_id: e.target.value })} className="input !py-1 text-sm w-36" title="Banco de onde sai">
                      <option value="">Banco…</option>
                      {contasBanco.map((c: any) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                    <button onClick={() => setDespesas((s) => s.filter((_, j) => j !== i))} className="text-red-600"><X size={14} /></button>
                  </div>
                  )
                })}
                <button onClick={() => setDespesas((s) => [...s, { descricao: '', valor: '', conta_gerencial_id: '', forma: 'dinheiro', data: '', conta_id: '' }])} className="flex items-center gap-1 text-xs text-brand-700"><Plus size={13} /> adicionar despesa</button>
              </div>

              {/* Totais */}
              <div className="bg-ink-50 rounded-lg p-3 text-sm grid grid-cols-2 md:grid-cols-5 gap-2">
                <div>
                  Recebido: <strong>{formatMoney(recebido)}</strong>
                  {recebidoExterno > 0 && (
                    <div className="text-[11px] text-emerald-700">
                      ↳ {formatMoney(recebidoExterno)} já baixado pelo financeiro
                    </div>
                  )}
                </div>
                <div>Previsto: <strong>{formatMoney(previstoEntregue)}</strong></div>
                <div className="text-emerald-700">Sobra: <strong>{formatMoney(totalSobra)}</strong></div>
                <div className="text-rose-700">Falta: <strong>{formatMoney(totalFalta)}</strong></div>
                <div className="text-amber-700">Desconto: <strong>{formatMoney(totalDesconto)}</strong></div>
                <div>Despesas: <strong>{formatMoney(totalDespesas)}</strong></div>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-between items-center gap-2 px-5 py-3 border-t sticky bottom-0 bg-white">
          <div className="text-xs text-ink-500">{allOk ? '✅ Tudo conferido' : '⚠ Salve o que já fez; pra FECHAR, todas as paradas verdes'} · dinheiro→caixa · pix→banco · cheque→carteira</div>
          <div className="flex items-center gap-2">
            {!fechada && (
              <>
                <button onClick={() => salvarRascunho.mutate()} disabled={salvarRascunho.isPending}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-300 text-ink-700 hover:bg-ink-50 text-sm font-medium disabled:opacity-50"
                  title="Salva o que já foi conferido SEM fechar a carga">
                  {salvarRascunho.isPending ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                  Salvar rascunho
                </button>
                <button onClick={imprimirConferencia} disabled={salvarRascunho.isPending}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-300 text-ink-700 hover:bg-ink-50 text-sm font-medium disabled:opacity-50"
                  title="Salva e imprime a conferência (rascunho) pra conferir antes de fechar">
                  <Printer size={15} /> Imprimir conferência
                </button>
              </>
            )}
            <button onClick={() => acertar.mutate()} disabled={acertar.isPending || !allOk}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50">
              {acertar.isPending ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
              Fechar carga
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ModalNovaCarga({ onClose, onCriada }: { onClose: () => void; onCriada: () => void }) {
  const [selecionados, setSelecionados] = useState<number[]>([])  // ordem = sequência
  const [veiculoId, setVeiculoId] = useState<string>('')
  const [rotaId, setRotaId] = useState<string>('')
  const [motoristaId, setMotoristaId] = useState<string>('')
  const [dataSaida, setDataSaida] = useState<string>('')
  const [nome, setNome] = useState<string>('')

  const ACEITOS = ['aberto', 'aprovado', 'separacao', 'embalagem', 'aguardando_nf']
  const { data: pedidos = [] } = useQuery<any[]>({
    queryKey: ['pedidos-disponiveis-carga'],
    queryFn: async () => (await api.get('/vendas')).data,
  })
  const disponiveis = (pedidos || []).filter((p) => ACEITOS.includes(p.status) && !p.carga_id)
  const { data: veiculos = [] } = useQuery<any[]>({ queryKey: ['exp-veiculos'], queryFn: async () => (await api.get('/expedicao/veiculos')).data })
  const { data: rotas = [] } = useQuery<any[]>({ queryKey: ['exp-rotas'], queryFn: async () => (await api.get('/expedicao/rotas')).data })
  const { data: motoristas = [] } = useQuery<any[]>({ queryKey: ['motoristas'], queryFn: async () => (await api.get('/expedicao/motoristas')).data })

  const toggle = (id: number) => setSelecionados((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])
  const mover = (idx: number, dir: -1 | 1) => setSelecionados((s) => {
    const n = [...s]; const j = idx + dir
    if (j < 0 || j >= n.length) return s
    ;[n[idx], n[j]] = [n[j], n[idx]]; return n
  })

  const criar = useMutation({
    mutationFn: async () => (await api.post('/vendas/cargas/distribuicao', {
      pedido_ids: selecionados,
      veiculo_id: veiculoId ? Number(veiculoId) : null,
      rota_id: rotaId ? Number(rotaId) : null,
      motorista_id: motoristaId ? Number(motoristaId) : null,
      data_saida: dataSaida || null,
      nome: nome || null,
    })).data,
    onSuccess: () => { toast.success('Carga montada'); onCriada() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao montar carga'),
  })

  const pedidoMap = new Map(disponiveis.map((p) => [p.id, p]))

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
          <h2 className="font-bold flex items-center gap-2"><Truck size={18} /> Nova carga de distribuição</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome/rota (opcional)" className="input col-span-2" />
            <input type="datetime-local" value={dataSaida} onChange={(e) => setDataSaida(e.target.value)} className="input" />
            <select value={veiculoId} onChange={(e) => setVeiculoId(e.target.value)} className="input">
              <option value="">Veículo…</option>
              {veiculos.map((v) => <option key={v.id} value={v.id}>{v.placa}{(v.tipo_carroceria || v.observacoes) ? ` — ${v.tipo_carroceria || v.observacoes}` : ''}{v.capacidade_kg ? ` (${v.capacidade_kg} kg)` : ''}</option>)}
            </select>
            <select value={motoristaId} onChange={(e) => setMotoristaId(e.target.value)} className="input">
              <option value="">Motorista…</option>
              {motoristas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
            <select value={rotaId} onChange={(e) => setRotaId(e.target.value)} className="input">
              <option value="">Rota…</option>
              {rotas.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>

          {/* Sequência selecionada */}
          {selecionados.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-ink-500 uppercase mb-1">Sequência de entrega ({selecionados.length})</div>
              <div className="space-y-1">
                {selecionados.map((id, idx) => {
                  const p = pedidoMap.get(id)
                  return (
                    <div key={id} className="flex items-center gap-2 bg-ink-50 rounded px-2 py-1 text-sm">
                      <span className="font-bold w-6">{idx + 1}</span>
                      <span className="flex-1">Pedido #{p?.numero} · {formatMoney(p?.total || 0)} · {p?.status}</span>
                      <button onClick={() => mover(idx, -1)} disabled={idx === 0} className="disabled:opacity-30"><ArrowUp size={14} /></button>
                      <button onClick={() => mover(idx, 1)} disabled={idx === selecionados.length - 1} className="disabled:opacity-30"><ArrowDown size={14} /></button>
                      <button onClick={() => toggle(id)} className="text-red-600"><X size={14} /></button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Pedidos disponíveis */}
          <div>
            <div className="text-xs font-semibold text-ink-500 uppercase mb-1">Pedidos disponíveis</div>
            <div className="border rounded max-h-64 overflow-auto divide-y">
              {disponiveis.length === 0 && <div className="p-3 text-sm text-ink-500">Nenhum pedido disponível (aprovado e sem carga).</div>}
              {disponiveis.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-ink-50">
                  <input type="checkbox" checked={selecionados.includes(p.id)} onChange={() => toggle(p.id)} />
                  <span className="flex-1">Pedido #{p.numero} · {formatMoney(p.total || 0)}</span>
                  <span className="text-xs text-ink-400">{p.status}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm">Cancelar</button>
          <button onClick={() => criar.mutate()} disabled={selecionados.length === 0 || criar.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
            {criar.isPending ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
            Montar carga ({selecionados.length})
          </button>
        </div>
      </div>
    </div>
  )
}


// Menu único "Impressões" da carga (dropdown fixo p/ escapar do overflow da tabela)
function ImpressoesMenuCarga({ items }: { items: { label: string; onClick: () => void }[] }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  if (!items.length) return null
  const toggle = () => {
    if (pos) { setPos(null); return }
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 210) })
  }
  return (
    <div className="inline-block">
      <button ref={btnRef} onClick={toggle}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium">
        <Printer size={14} /> Impressões <ChevronDown size={12} />
      </button>
      {pos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPos(null)} />
          <div className="fixed z-50 bg-white border border-ink-200 rounded-lg shadow-lg py-1 w-[220px] text-left"
            style={{ top: pos.top, left: pos.left }}>
            {items.map((it, idx) => (
              <button key={idx} onClick={() => { setPos(null); it.onClick() }}
                className="w-full text-left px-3 py-2 text-xs text-ink-700 hover:bg-slate-50 flex items-center gap-2">
                <FileText size={12} className="text-slate-500" /> {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
